import http from './http';

import { createLogger } from '../utils/logger';

const logger = createLogger('poolServiceProvider');
export interface PoolServiceProviderInfoRec {
  id: string;
  userId: string;
  companyName?: string;
  technicianName?: string;
  phoneNumber?: string;
  emergencyPhone?: string;
  email?: string;
  serviceAddress?: string;
  servicesProvided?: string;
  serviceFrequency?: string;
  costPerService?: number;
  seasonalService?: boolean;
  seasonStartDate?: string;
  seasonEndDate?: string;
  poolType?: string;
  poolSize?: string;
  equipmentMaintained?: string;
  chemicalsUsed?: string;
  emergencyService?: boolean;
  notes?: string;
  username?: string;
  password?: string;
  accountNumber?: string;
  isLocked?: boolean;
}

class PoolServiceProviderInfoService {
  async getAllPoolServiceProviderInfo(): Promise<PoolServiceProviderInfoRec[]> {
    const response = await http.get('/api/pool-service-provider-info');
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch pool service provider information');
    }

    return response.data.data || [];
  }

  async getPoolServiceProviderInfoById(id: string): Promise<PoolServiceProviderInfoRec | null> {
    try {
      const response = await http.get(`/api/pool-service-provider-info/${id}`);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch pool service provider information');
      }

      return response.data.data || null;
    } catch (error) {
      logger.error('Error fetching pool service provider information by ID:', error);
      return null;
    }
  }

  async createPoolServiceProviderInfo(poolServiceProviderData: Partial<PoolServiceProviderInfoRec>): Promise<PoolServiceProviderInfoRec> {
    const response = await http.post('/api/pool-service-provider-info', poolServiceProviderData);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to save pool service provider information');
    }

    return response.data.data;
  }

  async updatePoolServiceProviderInfo(id: string, poolServiceProviderData: Partial<PoolServiceProviderInfoRec>): Promise<PoolServiceProviderInfoRec> {
    const response = await http.patch(`/api/pool-service-provider-info/${id}`, poolServiceProviderData);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update pool service provider information');
    }

    return response.data.data;
  }

  async deletePoolServiceProviderInfo(id: string): Promise<void> {
    const response = await http.delete(`/api/pool-service-provider-info/${id}`);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete pool service provider information');
    }
  }

  async lockPoolServiceProviderInfo(id: string): Promise<void> {
    logger.debug('[Service Debug] Calling lock API for pool service provider ID:', id);
    const response = await http.post(`/api/pool-service-provider-info/${id}/lock`);
    logger.debug('[Service Debug] Lock API response:', response.data);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to lock pool service provider information');
    }
  }

  async unlockPoolServiceProviderInfo(id: string, granteeEmails: string[]): Promise<void> {
    logger.debug('[Service Debug] Calling unlock API for pool service provider ID:', id, 'granteeEmails:', granteeEmails);
    const response = await http.post(`/api/pool-service-provider-info/${id}/unlock`, { granteeEmails });
    logger.debug('[Service Debug] Unlock API response:', response.data);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to unlock pool service provider information');
    }
  }

  async getPoolServiceProviderAccessGrants(id: string): Promise<any[]> {
    try {
      const response = await http.get(`/api/pool-service-provider-info/${id}/access-grants`);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch pool service provider access grants');
      }

      return response.data.data || [];
    } catch (error) {
      logger.error('Error fetching pool service provider access grants:', error);
      return [];
    }
  }
}

export const poolServiceProviderInfoService = new PoolServiceProviderInfoService();
export default poolServiceProviderInfoService;